import React from 'react'

import * as S from './styles'
import { useFooterContainer } from './useFooterContainer'
import { Button } from '../../components/button'
import { Input } from '../../components/input'
import {
  WhatsAppIconWhite,
  WhatsAppIconBlack,
  LinkedinIconWhite,
  LinkedinIconBlack,
  InstagramIconWhite,
  InstagramIconBlack,
  FacebookIconWhite,
  FacebookIconBlack,
} from '@/assets/icons'
import { ButtonIcon, Text } from '@/components'
import { Footer } from '@/service/api/footerHeader'
import { validateEmail, validatePhone } from '@/utils/validations'

export type FooterProps = {
  footerData: Footer
}

export const FooterContainer: React.FC<FooterProps> = ({ footerData }) => {
  const {
    darkMode,
    handleContactSubmit,
    contactFormData,
    handleContactFormChange,
    handleErrorMessage,
    handleBlur,
    contactSubmitted,
    handleNewsletterFormChange,
    newsletterFormData,
    handleNewsletterSubmit,
    newsletterSubmitted,
    handleSocialMediaClick,
    isContactFormEmpty,
    isContactFormTouchedAndEmpty,
    isNewsletterFormEmpty,
    router,
  } = useFooterContainer()

  return (
    <S.FooterContainer darkMode={darkMode}>
      <S.FormContainer>
        <div className="form-contact">
          <div className="form-titles">
            <Text size="b1" color={darkMode ? 'white' : 'darkgray'}>
              {footerData.forms.contactFormTitle}
            </Text>
            <Text size="b3" color={darkMode ? 'white' : 'darkgray'}>
              {footerData.forms.contactFormSubtitle}
            </Text>
            {isContactFormTouchedAndEmpty() ? (
              <S.Error>
                {router.locale !== 'pt-BR'
                  ? 'At least one of the fields should be filled'
                  : 'Pelo menos um dos campos deve ser preenchido'}
              </S.Error>
            ) : null}
          </div>
          <form
            id="contact-form-site"
            name="Entre em contato - Site"
            onSubmit={handleContactSubmit}
          >
            <Input
              type="text"
              label={footerData.forms.nameField}
              name="name"
              placeholder={footerData.forms.namePlaceholder}
              onChange={handleContactFormChange}
              onBlur={() => handleBlur('name')}
              value={contactFormData.name}
              width="47.5%"
              darkMode={darkMode}
              error={handleErrorMessage(
                'name',
                contactFormData.name,
                undefined,
                undefined,
                false,
              )}
            />
            {/* The input below is a honey pot, do not remove */}
            <Input
              id="honey-pot"
              value={contactFormData?.lastname}
              placeholder="Sobrenome"
              label="Sobrenome"
              name="lastname"
              onChange={handleContactFormChange}
              type="text"
              className="h-input"
            />
            <Input
              type="email"
              label={footerData.forms.emailField}
              name="email"
              id="contactEmail"
              placeholder={footerData.forms.emailPlaceholder}
              onChange={handleContactFormChange}
              value={contactFormData.email}
              width="47.5%"
              darkMode={darkMode}
              onBlur={() => handleBlur('email')}
              error={handleErrorMessage(
                'email',
                contactFormData.email,
                validateEmail,
                footerData.forms.emailErrorMessage,
                false,
              )}
            />
            <Input
              type="tel"
              label={footerData.forms.phoneField}
              name="phone"
              maxLength={router.locale === 'pt-BR' ? 15 : 17}
              placeholder={footerData.forms.phonePlaceholder}
              onChange={handleContactFormChange}
              value={contactFormData.phone}
              width="47.5%"
              darkMode={darkMode}
              onBlur={() => handleBlur('phone')}
              error={handleErrorMessage(
                'phone',
                contactFormData.phone,
                validatePhone,
                footerData.forms.phoneErrorMessage,
              )}
            />
            {!contactSubmitted ? (
              <Button
                disabled={
                  isContactFormEmpty() ||
                  contactSubmitted ||
                  (!contactFormData.email && !contactFormData.phone) ||
                  (!!contactFormData.email &&
                    !validateEmail(contactFormData.email)) ||
                  (!!contactFormData.phone &&
                    !validatePhone(contactFormData.phone))
                }
              >
                {footerData.forms.sendButton}
              </Button>
            ) : (
              <span className="send-success">
                Informações enviadas com sucesso!
              </span>
            )}
          </form>
        </div>
        <div className="form-newsletter">
          <Text
            size="b1"
            className="newsletter-title"
            color={darkMode ? 'white' : 'darkgray'}
          >
            {footerData.forms.newsletterFormTitle}
          </Text>
          <Text size="b3" color={darkMode ? 'white' : 'darkgray'}>
            {footerData.forms.newsletterFormDescription}
          </Text>
          <form
            id="newsletter-form-site"
            name="Assinantes Newsletter - Site"
            style={{ width: '100%' }}
            onSubmit={handleNewsletterSubmit}
          >
            <Input
              type="email"
              label={footerData.forms.emailField}
              name="email"
              id="newsletterEmail"
              placeholder={footerData.forms.emailPlaceholder}
              onChange={handleNewsletterFormChange}
              value={newsletterFormData.email}
              width="100%"
              darkMode={darkMode}
              onBlur={() => handleBlur('newletterEmail')}
              error={handleErrorMessage(
                'newletterEmail',
                newsletterFormData.email,
                validateEmail,
                footerData.forms.emailErrorMessage,
              )}
            />
            <Input
              value={newsletterFormData?.lastname}
              placeholder="Sobrenome"
              label="Sobrenome"
              name="lastname"
              onChange={handleContactFormChange}
              type="text"
              className="h-input"
            />
            {!newsletterSubmitted ? (
              <Button
                disabled={
                  isNewsletterFormEmpty() ||
                  newsletterSubmitted ||
                  !validateEmail(newsletterFormData.email)
                }
                type="submit"
              >
                {footerData.forms.subscribeButton}
              </Button>
            ) : (
              <span className="send-success">
                Informações enviadas com sucesso!
              </span>
            )}
          </form>
        </div>
      </S.FormContainer>
      <hr className="divider" />
      <S.ContactContainer>
        <div className="contact-physical">
          <div>
            <Text size="b1" color={darkMode ? 'white' : 'darkgray'}>
              {footerData.contact.phoneTitle}
            </Text>
            {footerData.contact.phones &&
              footerData.contact.phones.map((phone) => (
                <Text
                  size="b3"
                  key={phone}
                  color={darkMode ? 'white' : 'darkgray'}
                >
                  {phone}
                </Text>
              ))}
          </div>
          <div>
            <Text size="b1" color={darkMode ? 'white' : 'darkgray'}>
              {footerData.contact.addressTitle}
            </Text>
            <Text
              size="b3"
              color={darkMode ? 'white' : 'darkgray'}
            >{`${footerData.contact.address.street}, ${footerData.contact.address.number}, ${footerData.contact.address.complement}`}</Text>
            <Text
              size="b3"
              color={darkMode ? 'white' : 'darkgray'}
            >{`${footerData.contact.address.city} - ${footerData.contact.address.state} - ${footerData.contact.address.country}`}</Text>
          </div>
        </div>
        <div className="contact-social">
          <Text
            size="b1"
            color={darkMode ? 'white' : 'darkgray'}
          >{`${footerData.contact.email}`}</Text>
          <div className="contact-icons">
            <ButtonIcon
              className="wa-icon"
              icon={darkMode ? WhatsAppIconWhite : WhatsAppIconBlack}
              alt="Botão para abrir Whatsapp da Nata"
              onClick={() =>
                handleSocialMediaClick(footerData.contact.whatsAppUrl)
              }
            />
            <ButtonIcon
              className="ln-icon"
              icon={darkMode ? LinkedinIconWhite : LinkedinIconBlack}
              alt="Botão para abrir Linkedin da Nata"
              onClick={() =>
                handleSocialMediaClick(footerData.contact.linkedinUrl)
              }
            />
            <ButtonIcon
              className="ig-icon"
              icon={darkMode ? InstagramIconWhite : InstagramIconBlack}
              alt="Botão para abrir Instagram da Nata"
              onClick={() =>
                handleSocialMediaClick(footerData.contact.instagramUrl)
              }
            />
            <ButtonIcon
              className="fb-icon"
              icon={darkMode ? FacebookIconWhite : FacebookIconBlack}
              alt="Botão para abrir Facebook da Nata"
              onClick={() =>
                handleSocialMediaClick(footerData.contact.facebookUrl)
              }
            />
          </div>
        </div>
      </S.ContactContainer>
    </S.FooterContainer>
  )
}
